/**
 * ItemFormFields Column 1 - Details
 *
 * Renders fields for the left column based on item type.
 * Layout varies by type but follows consistent row patterns.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Input, TextArea, Select } from '@/components/ui';
import { useUIStore, useSettingsStore, useItemStore } from '@/stores';
import { Plus, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import type { ItemType, SubItem } from '@perfin/shared';
import { getNearestHigherEffortStep, effortHoursToDisplay, getItemGroupsForCategoryAndSubCategory, RESERVED_ITEM_GROUPS } from '@perfin/shared';
import { EffortTimeStepper } from './EffortTimeStepper';

// ============================================================================
// TYPES
// ============================================================================

interface ItemFormFieldsCol1Props {
  type: ItemType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>;
  disabled?: boolean;
  statusOnlyEditable?: boolean;
  subItems?: SubItem[]; // For showing aggregated sub-item effort on tasks
  isSubItem?: boolean; // Sub-items don't have itemGroup
}

// ============================================================================
// OPTIONS
// ============================================================================

const priorityOptions = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'future', label: 'Future' },
];

const taskStatusOptions = [
  { value: 'new', label: 'New' },
  { value: 'inProgress', label: 'In Progress' },
  { value: 'complete', label: 'Complete' },
];

const transactionStatusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'final', label: 'Final' },
];

const noteKeyDateStatusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'final', label: 'Final' },
];

const currencyOptions = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'INR', label: 'INR (₹)' },
  { value: 'JPY', label: 'JPY (¥)' },
  { value: 'CAD', label: 'CAD ($)' },
  { value: 'AUD', label: 'AUD ($)' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function ItemFormFieldsCol1({ type, form, disabled = false, statusOnlyEditable = false, subItems = [], isSubItem = false }: ItemFormFieldsCol1Props) {
  // When statusOnlyEditable is true, all fields except status are disabled
  const fieldDisabled = disabled || statusOnlyEditable;

  // Calculate aggregated sub-item effort for tasks
  const subItemEffortTotal = subItems.reduce((sum, si) => {
    const effort = (si as unknown as { effortHours?: number }).effortHours || 0;
    return sum + effort;
  }, 0);
  const subItemEffortDisplay = subItemEffortTotal > 0
    ? effortHoursToDisplay(getNearestHigherEffortStep(subItemEffortTotal))
    : null;
  const { register, watch, setValue, formState: { errors } } = form;
  const { getCategoriesForType, members } = useSettingsStore();
  const { entities, accounts } = useSettingsStore();
  const { showConfirm } = useUIStore();
  const navigate = useNavigate();
  const items = useItemStore((state) => state.items);

  const isChoreWheel = watch('choreWheel');
  const recurrence = watch('recurrence');
  const selectedCategory = watch('category');
  const selectedSubCategory = watch('subCategory');
  const selectedItemGroup = watch('itemGroup');
  const transactionType = watch('transactionType');
  const selectedEntityId = watch('entityId');

  // State for new ItemGroup input
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // Get available itemGroups for selected category+subCategory
  const availableItemGroups = useMemo(() => {
    if (isSubItem || !selectedCategory || selectedCategory === 'None') return [];
    return getItemGroupsForCategoryAndSubCategory(items, selectedCategory, selectedSubCategory);
  }, [items, selectedCategory, selectedSubCategory, isSubItem]);

  // Track previous category/subCategory to detect actual changes
  const prevCategoryRef = useRef(selectedCategory);
  const prevSubCategoryRef = useRef(selectedSubCategory);
  const initializedRef = useRef(false);

  // Cascade: Clear itemGroup only when category or subCategory actually changes
  useEffect(() => {
    const categoryChanged = prevCategoryRef.current !== selectedCategory;
    const subCategoryChanged = prevSubCategoryRef.current !== selectedSubCategory;

    // Update refs
    prevCategoryRef.current = selectedCategory;
    prevSubCategoryRef.current = selectedSubCategory;

    // Skip first ACTUAL change (draft restoration sets category from 'None' to saved value)
    if (!initializedRef.current) {
      if (categoryChanged || subCategoryChanged) {
        initializedRef.current = true;
      }
      return;
    }

    // Only clear itemGroup when category/subCategory actually changed by user
    if (!isSubItem && (categoryChanged || subCategoryChanged)) {
      const currentGroup = form.getValues('itemGroup');
      if (currentGroup && currentGroup !== 'None') {
        const validGroups = getItemGroupsForCategoryAndSubCategory(items, selectedCategory, selectedSubCategory);
        if (!validGroups.includes(currentGroup)) {
          setValue('itemGroup', 'None');
        }
      }
    }
  }, [selectedCategory, selectedSubCategory, isSubItem, items, setValue, form]);

  // Toggle handlers
  const handleChoreWheelToggle = () => {
    if (isChoreWheel) {
      setValue('choreWheel', false);
    } else {
      if (!recurrence || recurrence === 'none') {
        showConfirm({
          title: 'Cannot Enable Chore',
          message: 'Please set a Recurrence first before converting this task to a chore.',
          cancelText: 'Close',
          variant: 'info',
          onConfirm: () => {},
        });
      } else {
        setValue('choreWheel', true);
      }
    }
  };

  // Dropdown options - filter categories by item type
  const filteredCategories = getCategoriesForType(type);
  const categoryOptions = [
    { value: 'None', label: 'None' },
    ...filteredCategories.map((cat) => ({ value: cat.id, label: cat.name })),
  ];

  const selectedCategoryObj = filteredCategories.find((c) => c.id === selectedCategory);
  const subCategoryOptions = [
    { value: 'None', label: 'None' },
    ...(selectedCategoryObj?.subCategories || []).map((sub) => ({
      value: sub.id,
      label: sub.name,
    })),
  ];

  const ownerOptions = [
    { value: 'None', label: 'None' },
    ...members.map((member) => ({
      value: member.userId,
      label: member.displayName || member.userId,
    })),
  ];

  const entityOptions = [
    { value: 'None', label: 'None' },
    ...entities.filter((e) => e.isActive).map((entity) => ({
      value: entity.id,
      label: entity.name,
    })),
  ];

  const accountOptions = [
    { value: 'None', label: 'None' },
    ...accounts
      .filter((a) => a.isActive && a.entityId === selectedEntityId)
      .map((account) => ({
        value: account.id,
        label: account.accountNumber
          ? `${account.accountName} (****${account.accountNumber.slice(-4)})`
          : account.accountName,
      })),
  ];

  const getStatusOptions = () => {
    switch (type) {
      case 'task': return taskStatusOptions;
      case 'transaction': return transactionStatusOptions;
      case 'note':
      case 'keyDate': return noteKeyDateStatusOptions;
      default: return [];
    }
  };

  const handleAddEntity = () => {
    navigate('/settings?tab=entities');
  };

  const handleAddAccount = () => {
    navigate('/settings?tab=accounts');
  };

  // Toggle button style helper
  const toggleButtonClass = (active: boolean, color: 'blue' | 'purple' = 'blue') => {
    const colorClasses = color === 'purple'
      ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700 hover:border-purple-700'
      : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:border-blue-700';
    return `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap border ${
      active
        ? colorClasses
        : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
    } ${fieldDisabled ? 'opacity-50 cursor-not-allowed' : ''}`;
  };

  // Radio button style helper
  const radioButtonClass = (active: boolean) => `
    flex-1 px-4 py-2 text-sm font-medium transition-colors text-center cursor-pointer
    ${active
      ? 'bg-blue-600 text-white'
      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }
    ${fieldDisabled ? 'opacity-50 cursor-not-allowed' : ''}
  `;

  return (
    <div className="space-y-4">
      {/* Row 1: Owner + Status (all types) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select
          label="Owner"
          options={ownerOptions}
          disabled={fieldDisabled}
          {...register('owner')}
        />
        {/* For recurring items, show "Per Occurrence" text instead of Status dropdown */}
        {recurrence && recurrence !== 'none' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <div className="px-3 py-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg">
              Per Occurrence
            </div>
          </div>
        ) : (
          <Select
            label="Status"
            options={getStatusOptions()}
            disabled={disabled}
            {...register('status')}
          />
        )}
      </div>

      {/* Row 2: Category + SubCategory + ItemGroup (Items only) */}
      {/* SubItems: 2 columns (50/50), Items: 3 columns (1/3 each) */}
      {isSubItem ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Category"
            options={categoryOptions}
            disabled={fieldDisabled}
            {...register('category')}
          />
          <Select
            label="Sub-category"
            options={subCategoryOptions}
            disabled={fieldDisabled || !selectedCategory || selectedCategory === 'None'}
            {...register('subCategory')}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Select
            label="Category"
            options={categoryOptions}
            disabled={fieldDisabled}
            {...register('category')}
          />
          <Select
            label="Sub-category"
            options={subCategoryOptions}
            disabled={fieldDisabled || !selectedCategory || selectedCategory === 'None'}
            {...register('subCategory')}
          />
          {/* ItemGroup - Combo dropdown */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">Item Group</label>
            {isCreatingGroup ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Name..."
                  className="min-w-0 flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const trimmed = newGroupName.trim();
                      if (trimmed && !RESERVED_ITEM_GROUPS.includes(trimmed)) {
                        setValue('itemGroup', trimmed);
                        setNewGroupName('');
                        setIsCreatingGroup(false);
                      }
                    } else if (e.key === 'Escape') {
                      setNewGroupName('');
                      setIsCreatingGroup(false);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const trimmed = newGroupName.trim();
                    if (trimmed && !RESERVED_ITEM_GROUPS.includes(trimmed)) {
                      setValue('itemGroup', trimmed);
                      setNewGroupName('');
                      setIsCreatingGroup(false);
                    }
                  }}
                  disabled={!newGroupName.trim() || RESERVED_ITEM_GROUPS.includes(newGroupName.trim())}
                  className="shrink-0 p-1.5 text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNewGroupName('');
                    setIsCreatingGroup(false);
                  }}
                  className="shrink-0 p-1.5 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <select
                value={selectedItemGroup || 'None'}
                onChange={(e) => {
                  if (e.target.value === '__CREATE_NEW__') {
                    setIsCreatingGroup(true);
                  } else {
                    setValue('itemGroup', e.target.value);
                  }
                }}
                disabled={fieldDisabled || !selectedCategory || selectedCategory === 'None'}
                className={`w-full px-4 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${
                  fieldDisabled || !selectedCategory || selectedCategory === 'None'
                    ? 'bg-gray-100 cursor-not-allowed'
                    : 'bg-white'
                }`}
              >
                <option value="None">None</option>
                {/* Show current custom group if not in available list */}
                {selectedItemGroup && selectedItemGroup !== 'None' && !availableItemGroups.includes(selectedItemGroup) && (
                  <option value={selectedItemGroup}>{selectedItemGroup}</option>
                )}
                {availableItemGroups.map((group) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
                <option value="__CREATE_NEW__">+ Create New...</option>
              </select>
            )}
          </div>
        </div>
      )}

      {/* Row 3: Priority + Effort Time + Chore (task items only, NOT sub-items) */}
      {/* Phone: Priority on own row, Effort + Chore on second row */}
      {/* Desktop: Priority and Effort share equal space, Chore gets auto width */}
      {type === 'task' && !isSubItem && (
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="w-full sm:flex-1">
            <Select
              label="Priority"
              options={priorityOptions}
              disabled={fieldDisabled}
              {...register('priority')}
            />
          </div>
          <div className="flex items-end gap-3 sm:flex-1">
            <div className="flex-1">
              <div className="flex items-baseline gap-1.5 mb-1">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Effort (Time)</label>
                {subItemEffortDisplay && (
                  <span className="text-xs text-green-600 font-medium whitespace-nowrap">
                    · Subs: {subItemEffortDisplay}
                  </span>
                )}
              </div>
              <Controller
                name="effortHours"
                control={form.control}
                render={({ field }) => (
                  <EffortTimeStepper
                    value={field.value}
                    onChange={field.onChange}
                    disabled={fieldDisabled}
                  />
                )}
              />
            </div>
            <button
              type="button"
              disabled={fieldDisabled}
              onClick={handleChoreWheelToggle}
              className={toggleButtonClass(isChoreWheel, 'purple')}
            >
              Chore
            </button>
          </div>
        </div>
      )}

      {/* Row 3: Priority + Effort Time (task sub-items - no Chore toggle) */}
      {type === 'task' && isSubItem && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Priority"
            options={priorityOptions}
            disabled={fieldDisabled}
            {...register('priority')}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Effort (Time)</label>
            <Controller
              name="effortHours"
              control={form.control}
              render={({ field }) => (
                <EffortTimeStepper
                  value={field.value}
                  onChange={field.onChange}
                  disabled={fieldDisabled}
                />
              )}
            />
          </div>
        </div>
      )}

      {/* Row 3: Priority + Effort Time (keyDate, note, transaction) */}
      {(type === 'keyDate' || type === 'note' || type === 'transaction') && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Priority"
            options={priorityOptions}
            disabled={fieldDisabled}
            {...register('priority')}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Effort (Time)</label>
            <Controller
              name="effortHours"
              control={form.control}
              render={({ field }) => (
                <EffortTimeStepper
                  value={field.value}
                  onChange={field.onChange}
                  disabled={fieldDisabled}
                />
              )}
            />
          </div>
        </div>
      )}

      {/* Transaction-specific fields */}
      {type === 'transaction' && (
        <>
          {/* Transaction Type - Radio Buttons */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Transaction Type</label>
            <div className="flex rounded-lg overflow-hidden border border-gray-300">
              <button
                type="button"
                disabled={fieldDisabled}
                onClick={() => setValue('transactionType', 'expense')}
                className={radioButtonClass(transactionType === 'expense')}
              >
                Expense
              </button>
              <button
                type="button"
                disabled={fieldDisabled}
                onClick={() => setValue('transactionType', 'income')}
                className={radioButtonClass(transactionType === 'income')}
              >
                Income
              </button>
            </div>
          </div>

          {/* Amount + Currency */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              disabled={fieldDisabled}
              error={errors.amount?.message as string | undefined}
              {...register('amount', {
                required: 'Amount is required',
                min: { value: 0, message: 'Amount must be positive' },
                validate: (value) => {
                  const num = parseFloat(value);
                  if (isNaN(num)) return 'Invalid amount';
                  // Check max 2 decimal places
                  const decimalPart = value.toString().split('.')[1];
                  if (decimalPart && decimalPart.length > 2) {
                    return 'Maximum 2 decimal places allowed';
                  }
                  return true;
                },
              })}
            />
            <Select
              label="Currency"
              options={currencyOptions}
              disabled={fieldDisabled}
              {...register('currency')}
            />
          </div>

          {/* Entity + Account */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Select
                  label="Entity"
                  options={entityOptions}
                  disabled={fieldDisabled}
                  {...register('entityId')}
                />
              </div>
              <button
                type="button"
                onClick={handleAddEntity}
                disabled={fieldDisabled}
                className="mb-0.5 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Add Entity"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Select
                  label="Account"
                  options={accountOptions}
                  disabled={fieldDisabled || !selectedEntityId || selectedEntityId === 'None'}
                  {...register('accountId')}
                />
              </div>
              <button
                type="button"
                onClick={handleAddAccount}
                disabled={fieldDisabled || !selectedEntityId || selectedEntityId === 'None'}
                className="mb-0.5 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Add Account"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Name + Description (task, keyDate, note - NOT transaction) */}
      {type !== 'transaction' && (
        <>
          <Input
            label="Name"
            placeholder="Enter name..."
            disabled={fieldDisabled}
            error={errors.name?.message as string | undefined}
            {...register('name', { required: 'Name is required' })}
          />
          <TextArea
            label="Description"
            placeholder="Add details..."
            rows={3}
            disabled={fieldDisabled}
            {...register('description')}
          />
        </>
      )}
    </div>
  );
}
